import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader, SectionLabel } from "@/components/Primitives";
import { Loader2, Search } from "lucide-react";

export default function SearchHistory() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/history/searches");
        setQueries(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return queries.filter((q) => {
      if (statusFilter !== "all" && q.status !== statusFilter) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        return (
          (q.label || "").toLowerCase().includes(s) ||
          String(q.latitude).includes(s) ||
          String(q.longitude).includes(s)
        );
      }
      return true;
    });
  }, [queries, search, statusFilter]);

  return (
    <div className="px-8 md:px-12 py-10 max-w-[1400px]">
      <PageHeader
        eyebrow="Module / 03"
        title="Search History"
        description="Every audience query — single and bulk — recorded with status, coordinates, and time window selections."
        testid="history-header"
      />

      <div className="flex flex-col md:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
          <input
            data-testid="history-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter by label or coordinate"
            className="w-full bg-white border border-[#E0E0D8] rounded-none pl-9 pr-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
          />
        </div>
        <select
          data-testid="history-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-white border border-[#E0E0D8] rounded-none px-3 py-2.5 font-mono text-sm focus:border-[#FF3E00] focus:outline-none"
        >
          <option value="all">All statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#555]">
          <Loader2 className="animate-spin" size={16} /> Loading queries…
        </div>
      ) : (
        <div className="border border-[#E0E0D8] bg-white overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b-2 border-[#0A0A0A]">
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">When</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Label</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Lat / Lon</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Radii</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Windows</th>
                <th className="text-left py-3 px-4 font-mono text-[11px] uppercase tracking-wider text-[#555]">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((q) => (
                <tr key={q.id} className="border-b border-[#E0E0D8] hover:bg-[#F4F4F0]" data-testid={`history-row-${q.id}`}>
                  <td className="py-3 px-4 font-mono text-xs text-[#555]">{new Date(q.created_at).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm">{q.label || "—"}</td>
                  <td className="py-3 px-4 font-mono text-xs">{q.latitude}, {q.longitude}</td>
                  <td className="py-3 px-4 font-mono text-xs">{(q.radii || []).join(" · ")}</td>
                  <td className="py-3 px-4 font-mono text-xs">{(q.time_windows || []).join(" · ")}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${
                      q.status === "completed" ? "text-[#00994D] border-[#00994D]/30 bg-[#00994D]/10" :
                      q.status === "failed"    ? "text-[#D91919] border-[#D91919]/30 bg-[#D91919]/10" :
                                                  "text-[#555] border-[#E0E0D8] bg-[#F4F4F0]"
                    }`}>
                      {q.status}
                    </span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="py-12 text-center text-sm text-[#888] font-mono">No queries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
